import { Table, Thead, Tbody, Tr, Th, Td, Skeleton, Box } from '@chakra-ui/react';
import { useReactTable, flexRender, getCoreRowModel, ColumnDef, Table as TableType } from '@tanstack/react-table';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { EmptyTableMascot, IEmptyTableMascotProps } from 'components/Mascot';
import ReactTablePagination from 'components/Table/ReactTablePagination';
import TablePagination from 'components/Table/TablePagination';

import DraggableRow from './DraggableRow';

interface IPaginationData {
  current_page: number;
  first_page_url: string;
  from: number;
  last_page: number;
  last_page_url: string;
  next_page_url: string;
  prev_page_url: string;
  path: string;
  per_page: string;
  total: number;
  to: number;
  options: boolean;
}

export type DataTableProps = {
  renderTableFooter?: (selectedIds: string[], toggleAllRowsSelected: (value?: boolean) => void) => React.ReactNode;
  handlePageSizeChange?: (pageNumber: number) => void;
  onPageChange?: (pageIndex: number) => void;
  enableRowSelect?: boolean | undefined;
  data: any[];
  columns: ColumnDef<any, any>[];
  isLoading?: boolean;
  paginationData?: IPaginationData;
  paginationStyle?: 'default' | 'rowsPerPage';
  onTableInstanceCreated?: (tableInstance: TableType<any>) => void;
  emptyTableStateProps?: IEmptyTableMascotProps;
};

export default function TanstackTable({
  renderTableFooter,
  data,
  columns,
  isLoading,
  paginationData,
  enableRowSelect = false,
  paginationStyle = 'default',
  emptyTableStateProps,
  onTableInstanceCreated,
  onPageChange,
  handlePageSizeChange,
}: DataTableProps) {
  const { t } = useTranslation(['components']);

  // const [rowSelection, setRowSelection] = React.useState({});

  const table = useReactTable({
    columns,
    data,
    getRowId: row => row.id,
    getCoreRowModel: getCoreRowModel(),
    enableRowSelection: enableRowSelect,
    debugTable: true,
    debugHeaders: true,
    debugColumns: true,
    // state: {
    //   rowSelection,
    // },
    autoResetAll: true,
  });

  const pageCount = paginationData?.last_page ?? 1;
  const totalItems = paginationData?.total;
  const rowsPerPage = paginationData?.per_page ?? 10;
  const currentPage = paginationData?.current_page ? paginationData?.current_page - 1 : 0;

  useEffect(() => {
    if (onTableInstanceCreated) {
      onTableInstanceCreated(table);
    }
  }, [onTableInstanceCreated, table]);

  return (
    <Box mt="6">
      {totalItems ? (
        <Box textAlign="right" mb="1">
          {t('components:showingXOfYEntries', { length: data.length, total: totalItems })}
        </Box>
      ) : null}
      <Box overflowX="auto">
        <Table>
          <Thead>
            {table.getHeaderGroups().map(headerGroup => (
              <Tr
                key={headerGroup.id}
                display="flex"
                border="1px solid"
                borderColor="primary.100"
                borderTopRightRadius="lg"
                borderTopLeftRadius="lg"
                overflow="hidden"
              >
                {headerGroup.headers.map(header => {
                  return (
                    <Th
                      key={header.id}
                      bg="gray.100"
                      py={{ base: '2', md: '4' }}
                      px={{ base: '4', md: '4' }}
                      width={header.getSize()}
                      flex="1 0 auto"
                    >
                      {flexRender(header.column.columnDef.header, header.getContext())}
                    </Th>
                  );
                })}
              </Tr>
            ))}
          </Thead>
          <Tbody position="relative">
            {isLoading ? (
              Array.from(new Array(+rowsPerPage)).map((_headerGroup, index) => {
                return (
                  <Tr
                    key={index}
                    mb="3"
                    flexDir={{ base: 'column', md: 'row' }}
                    border={{ base: '1px', md: '0' }}
                    borderRadius="lg"
                    borderColor="primary.100"
                    py={{ base: '3', md: '0' }}
                    display="flex"
                  >
                    {table.getAllLeafColumns().map(column => {
                      return (
                        <Td
                          key={column.id}
                          py={{ base: '2', md: '4' }}
                          px={{ base: '4', md: '4' }}
                          display="flex"
                          alignItems="center"
                          width={column.getSize()}
                          flex="1 0 auto"
                        >
                          <Skeleton flex="0 0 120px" mr="3" height="17px" display={{ base: 'block', md: 'none' }} />
                          <Skeleton flex="1" height="17px" />
                        </Td>
                      );
                    })}
                  </Tr>
                );
              })
            ) : data.length ? (
              <>
                {table.getRowModel().rows.map(row => (
                  <DraggableRow key={row.id} row={row} />
                ))}
              </>
            ) : (
              <Tr>
                <Td colSpan={columns.length} display="flex" alignItems="center" justifyContent="center">
                  <EmptyTableMascot description="No records found" {...emptyTableStateProps} />
                </Td>
              </Tr>
            )}
          </Tbody>
        </Table>
        {renderTableFooter && table.getState().rowSelection
          ? renderTableFooter(Object.keys(table.getState().rowSelection), table.resetRowSelection)
          : null}
      </Box>

      {/* Render pagination */}
      {pageCount && totalItems !== 0 && onPageChange && !isLoading ? (
        paginationStyle === 'default' ? (
          <TablePagination pageCount={pageCount} onPageChange={onPageChange} currentPage={currentPage} />
        ) : (
          <ReactTablePagination
            pageCount={pageCount}
            onPageChange={onPageChange}
            currentPage={currentPage}
            totalItems={totalItems || 0}
            rowsPerPage={rowsPerPage as number}
            handlePageSizeChange={handlePageSizeChange}
          />
        )
      ) : null}
    </Box>
  );
}
